import { type ReactNode } from "react";
import {
  daCredito,
  percentualEmTexto,
  temCreditoPresumido,
  temReducao,
  type Classificacao,
} from "../lib/tabelaCst";

// As peças do CST e ClassTrib (000223).
//
// ---------------------------------------------------------------------------
// O que esta tela faz diferente da origem, e por quê
// ---------------------------------------------------------------------------
// A ferramenta de origem (um app da Lovable) mostra os indicadores como SEIS
// colunas de bolinhas verdes ou cinzas, com o rótulo só no cabeçalho da tabela.
// O problema não é estética: **bolinha cinza não diz o que é**. Ela pode
// significar "não tem", "não se aplica" ou "a Receita não informou", e as três
// levam o contador a condutas diferentes.
//
// Aqui o indicador que está LIGADO vira um chip com o nome dele, e o que está
// desligado não aparece. Uma classificação com dois chips mostra duas coisas
// verdadeiras; a mesma linha com seis bolinhas, quatro delas cinzas, mostra
// quatro afirmações que ninguém fez.
//
// Onde o valor é um NÚMERO (os percentuais de redução), a régua entra — porque
// ali a comparação entre 30%, 60% e 100% é o conteúdo.

/** O chip de um indicador LIGADO. Desligado não vira chip. */
export function Chip({
  children,
  tom = "neutro",
  titulo,
}: {
  children: ReactNode;
  tom?: "neutro" | "acento" | "alerta" | "sucesso";
  titulo?: string;
}) {
  const cores = {
    neutro: "bg-chip text-cinza",
    acento: "bg-acento/12 text-acento",
    alerta: "bg-alerta-fundo text-alerta-tinta",
    sucesso: "bg-aviso-fundo text-aviso-tinta",
  } as const;
  return (
    <span
      title={titulo}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${cores[tom]}`}
    >
      {children}
    </span>
  );
}

/** O código, em monoespaçado — é o que o aluno copia para a nota. */
export function Codigo({
  children,
  grande = false,
}: {
  children: ReactNode;
  grande?: boolean;
}) {
  return (
    <span
      className={`rounded-lg bg-acento/12 font-mono font-bold text-acento tabular-nums ${
        grande ? "px-3 py-1.5 text-base" : "px-2 py-0.5 text-xs"
      }`}
    >
      {children}
    </span>
  );
}

/**
 * A estrela do favorito.
 *
 * `aria-pressed` e não só a cor: quem usa leitor de tela precisa saber o ESTADO,
 * e a estrela cheia contra a vazia é diferença puramente visual. E o rótulo
 * muda com o estado — "Favoritar" e "Remover dos favoritos" são ações
 * diferentes, e um rótulo fixo obrigaria a pessoa a adivinhar qual vai
 * acontecer.
 */
export function Estrela({
  marcada,
  aoClicar,
  ocupada = false,
  rotulo,
}: {
  marcada: boolean;
  aoClicar: () => void;
  ocupada?: boolean;
  rotulo: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={marcada}
      aria-label={marcada ? `Remover ${rotulo} dos favoritos` : `Favoritar ${rotulo}`}
      title={marcada ? "Remover dos favoritos" : "Favoritar"}
      disabled={ocupada}
      onClick={(e) => {
        // A linha inteira é clicável (ela abre a ficha). Sem isto, favoritar
        // também abriria — e o aluno perderia o lugar na lista a cada estrela.
        e.stopPropagation();
        aoClicar();
      }}
      className={`shrink-0 rounded-lg p-1.5 transition disabled:opacity-40 ${
        marcada ? "text-laranja hover:bg-laranja/10" : "text-cinza hover:bg-chip hover:text-laranja"
      }`}
    >
      <svg
        viewBox="0 0 24 24"
        width="1.15em"
        height="1.15em"
        aria-hidden="true"
        fill={marcada ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth={marcada ? 0 : 1.8}
        strokeLinejoin="round"
      >
        <path d="M12 2.8l2.83 5.73 6.32.92-4.57 4.46 1.08 6.3L12 17.24l-5.66 2.97 1.08-6.3-4.57-4.46 6.32-.92L12 2.8z" />
      </svg>
    </button>
  );
}

/**
 * A régua de redução.
 *
 * Só aparece quando HÁ redução. Uma barra em zero, repetida em 101 das 164
 * classificações, seria ruído com aparência de informação — e a diferença entre
 * "reduz 0%" e "não tem redução" não existe para quem lê.
 */
export function ReguaDeReducao({
  rotulo,
  percentual,
}: {
  rotulo: string;
  percentual: number | null | undefined;
}) {
  const p = typeof percentual === "number" && Number.isFinite(percentual) ? percentual : null;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold text-cinza">{rotulo}</span>
        <span className="text-sm font-bold text-titulo tabular-nums">
          {percentualEmTexto(percentual)}
        </span>
      </div>
      {p !== null && (
        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-chip">
          <div
            className="h-full rounded-full bg-acento transition-[width]"
            style={{ width: `${Math.max(0, Math.min(100, p))}%` }}
          />
        </div>
      )}
    </div>
  );
}

/** Uma linha de fato na ficha: rótulo à esquerda, valor à direita. */
export function Fato({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-borda py-2.5 last:border-0">
      <span className="text-xs font-semibold text-cinza">{rotulo}</span>
      <span className="text-right text-sm text-tinta">{children}</span>
    </div>
  );
}

/**
 * Os chips de uma classificação — só o que está LIGADO.
 *
 * `compacto` mostra os dois que mudam a conduta na hora de escolher (reduz,
 * dá crédito); a ficha mostra todos. A lista com seis chips por linha viraria
 * a parede de bolinhas da origem com outra roupa.
 */
export function ChipsDaClassificacao({
  c,
  compacto = false,
}: {
  c: Classificacao;
  compacto?: boolean;
}) {
  const chips: ReactNode[] = [];

  if (temReducao(c)) {
    const maior = Math.max(
      c.percentualReducaoCbs ?? 0,
      c.percentualReducaoIbsUf ?? 0,
      c.percentualReducaoIbsMun ?? 0,
    );
    chips.push(
      <Chip key="red" tom="acento" titulo="Esta classificação reduz a alíquota">
        {maior > 0 ? `reduz ${percentualEmTexto(maior)}` : "com redução"}
      </Chip>,
    );
  }
  if (daCredito(c)) {
    chips.push(
      <Chip key="cred" tom="sucesso" titulo="O adquirente se apropria do crédito">
        dá crédito
      </Chip>,
    );
  }

  if (!compacto) {
    if (temCreditoPresumido(c)) {
      chips.push(
        <Chip key="pres" tom="sucesso" titulo="Gera crédito presumido">
          crédito presumido
        </Chip>,
      );
    }
    if (c.incompativelComSuspensao) {
      chips.push(
        <Chip key="susp" tom="alerta" titulo="Não convive com o regime de suspensão">
          incompatível com suspensão
        </Chip>,
      );
    }
    if (c.exigeGrupoDesoneracao) {
      chips.push(
        <Chip key="deson" tom="alerta" titulo="A nota precisa do grupo de desoneração">
          exige grupo de desoneração
        </Chip>,
      );
    }
  }

  // A classificação SEM FICHA: a lista por CST a tem e o catálogo não. Ela
  // aparece marcada, e não some nem finge estar completa — as duas fontes do
  // gov discordam, e é ele quem tem de resolver, não a tela.
  if (c.semFicha) {
    chips.push(
      <Chip
        key="sf"
        tom="alerta"
        titulo="A Receita lista este código no CST, mas não publica a ficha dele nesta data"
      >
        sem ficha nesta data
      </Chip>,
    );
  }

  if (chips.length === 0) return null;
  return <div className="flex flex-wrap items-center gap-1.5">{chips}</div>;
}

/** O aviso de que o piloto do gov não respondeu. Nada a corrigir — é esperar. */
export function GovForaDoAr({ motivo, aoTentar }: { motivo: string; aoTentar?: () => void }) {
  const explica =
    motivo === "tempo"
      ? "A Receita demorou demais para responder."
      : motivo === "rede"
        ? "Não conseguimos alcançar a base da Receita."
        : motivo.startsWith("gov-")
          ? "A base da Receita respondeu com um erro interno."
          : "A base da Receita não respondeu como esperado.";
  return (
    <div className="rounded-cf-lg border border-alerta-tinta/40 bg-alerta-fundo p-5" role="alert">
      <p className="text-sm font-bold text-alerta-tinta">{explica}</p>
      <p className="mt-1 text-sm leading-relaxed text-alerta-tinta">
        A tabela de CST e classificações vem do ambiente de piloto da Receita Federal, que sai
        do ar de vez em quando. Não há nada a corrigir aqui — tente de novo em alguns minutos.
      </p>
      {aoTentar && (
        <button
          type="button"
          onClick={aoTentar}
          className="mt-3 rounded-lg border border-alerta-tinta/50 px-3 py-1.5 text-xs font-semibold text-alerta-tinta transition hover:bg-alerta-tinta/10"
        >
          Tentar de novo
        </button>
      )}
    </div>
  );
}

/** O bloco de "nada encontrado", com o caminho de volta. */
export function SemResultado({ aoLimpar }: { aoLimpar: () => void }) {
  return (
    <div className="rounded-cf-lg border border-borda bg-superficie p-8 text-center">
      <p className="text-sm font-semibold text-tinta">Nenhuma classificação com esses filtros</p>
      <p className="mt-1.5 text-sm leading-relaxed text-cinza">
        Tente uma palavra do texto da norma (cesta, medicamento, exportação), o código de um
        CST (410) ou o de uma classificação (200019).
      </p>
      <button
        type="button"
        onClick={aoLimpar}
        className="mt-4 rounded-lg border border-borda px-4 py-2 text-sm font-semibold text-tinta transition hover:border-laranja"
      >
        Limpar os filtros
      </button>
    </div>
  );
}
